/**
 * Motion maths for data transitions: the standard easing curve evaluated in code (so a chart can tween old → new values
 * with the same feel as the CSS entrance), interpolation of value lists and the count-up of a number. The durations
 * and the curve come from `@vello/tokens` — a test pins {@link EASE_STANDARD_POINTS} to `EASING.standard` so they cannot drift.
 */
import { clamp, isFiniteNumber, lerp } from './numbers.js';
/** The control points of `cubic-bezier(0.2, 0, 0, 1)`, the Vello standard ease (no bounce, no spring). */
export const EASE_STANDARD_POINTS = [0.2, 0, 0, 1];
/** A CSS-style cubic-bezier easing: maps linear time t ∈ [0, 1] to eased progress. */
export function cubicBezier(x1, y1, x2, y2) {
    const cx = 3 * x1;
    const bx = 3 * (x2 - x1) - cx;
    const ax = 1 - cx - bx;
    const cy = 3 * y1;
    const by = 3 * (y2 - y1) - cy;
    const ay = 1 - cy - by;
    const sampleX = (s) => ((ax * s + bx) * s + cx) * s;
    const sampleY = (s) => ((ay * s + by) * s + cy) * s;
    const slopeX = (s) => (3 * ax * s + 2 * bx) * s + cx;
    return (t) => {
        if (t <= 0)
            return 0;
        if (t >= 1)
            return 1;
        // Newton, then bisection as the fallback for flat slopes.
        let s = t;
        for (let i = 0; i < 8; i += 1) {
            const err = sampleX(s) - t;
            if (Math.abs(err) < 1e-6)
                return sampleY(s);
            const d = slopeX(s);
            if (Math.abs(d) < 1e-6)
                break;
            s -= err / d;
        }
        let lo = 0;
        let hi = 1;
        s = t;
        for (let i = 0; i < 24; i += 1) {
            const x = sampleX(s);
            if (Math.abs(x - t) < 1e-6)
                break;
            if (t > x)
                lo = s;
            else
                hi = s;
            s = (lo + hi) / 2;
        }
        return sampleY(s);
    };
}
/** The Vello standard easing as a function. */
export const easeStandard = cubicBezier(...EASE_STANDARD_POINTS);
/**
 * Interpolate two value lists. When the lists differ in length the missing side is taken from the other (a new item
 * grows from nothing only if the caller passes `grow`; by default it appears at its own value, so the existing items
 * still transition and nothing flies in from a stranger's position). A gap (`null`) on either side snaps to the target.
 */
export function lerpValues(from, to, t, grow) {
    const k = clamp(t, 0, 1);
    return to.map((target, i) => {
        if (target === null || !isFiniteNumber(target))
            return null;
        const start = i < from.length ? from[i] : grow;
        if (start === null || start === undefined || !isFiniteNumber(start))
            return target;
        return lerp(start, target, k);
    });
}
/**
 * FX-05 easing: ease-out cubic, `1 - (1 - t)^3` (the design count-up). Starts fast and settles; t outside [0, 1] is clamped.
 */
export function easeOutCubic(t) {
    const k = clamp(t, 0, 1);
    return 1 - (1 - k) ** 3;
}
/** The value shown by a count-up at progress `t` (eased by the caller), rounded to `decimals`. */
export function countUpValue(from, to, t, decimals = 0) {
    const k = clamp(t, 0, 1);
    const f = 10 ** Math.max(0, decimals);
    if (k >= 1)
        return to;
    return Math.round(lerp(from, to, k) * f) / f;
}
/** The entrance delay of the `index`-th item of a cascade: `index × step`, with the index capped so a long series stays quick. */
export function staggerDelay(index, stepMs, maxSteps = 6) {
    return Math.max(0, Math.min(Math.floor(index), maxSteps)) * stepMs;
}
//# sourceMappingURL=motion.js.map